import React, { useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import './heroVideo.scss'

const SRC = '/videos/hero.mp4'
const POSTER = '/videos/hero-poster.jpg'

/**
 * Silent looping background video behind the homepage hero, with a dark
 * overlay so white text stays readable on top of it.
 *
 * Decorative only: hidden from screen readers, no controls, no sound. The
 * poster frame is always painted underneath, so the hero looks complete
 * before (or without) the video. The video is skipped entirely for visitors
 * who ask for reduced motion or have data saving switched on, and it pauses
 * while scrolled out of view so it costs nothing further down the page.
 *
 * To use real footage, replace public/videos/hero.mp4 and hero-poster.jpg
 * (scripts/generate-hero-video.js renders the current ones).
 */
const HeroVideo = () => {
  const reduced = useReducedMotion()
  const videoRef = useRef(null)
  const [saveData] = useState(() => typeof navigator !== 'undefined' && navigator.connection && navigator.connection.saveData === true)
  const play = !reduced && !saveData

  useEffect(() => {
    const video = videoRef.current
    if (!video || !play || typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const attempt = video.play()
        if (attempt && attempt.catch) attempt.catch(() => {}) // autoplay blocked: the poster stays
      } else {
        video.pause()
      }
    }, { threshold: 0.05 })
    observer.observe(video)
    return () => observer.disconnect()
  }, [play])

  return (
    <div className='hero-video' aria-hidden='true' style={{ backgroundImage: `url(${POSTER})` }}>
      {play && (
        <video ref={videoRef} className='hero-video-media' muted loop playsInline autoPlay preload='metadata' poster={POSTER}>
          <source src={SRC} type='video/mp4' />
        </video>
      )}
      <div className='hero-video-overlay' />
    </div>
  )
}

export default HeroVideo
